<template>
  <VaDropdown
    :offset="[13, 0]"
    class="recruitment-notification-dropdown"
    stick-to-edges
    :close-on-content-click="false"
  >
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge overlap>
          <template #text>{{ unreadCount > 0 ? unreadCount : '' }}</template>
          <VaIcon name="person_add" size="22px" style="color: rgb(1, 237, 3)" />
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[400px] sm:h-auto">
      <div class="p-3 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Recruitment Notifications</h3>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">New drivers & applicant state changes</p>
      </div>
      <section class="sm:max-h-[400px] overflow-auto">
        <VaList class="space-y-0">
          <template v-for="item in notificationsWithRelativeTime" :key="item.id">
            <VaListItem
              class="text-sm cursor-pointer py-3 px-4 hover:bg-gray-50 dark:hover:bg-gray-800"
              :class="{ 'bg-blue-50 dark:bg-blue-900/20': !item.seen }"
              @click="handleNotificationClick(item)"
            >
              <VaListItemSection icon class="flex-shrink-0">
                <VaIcon
                  :name="item.type === 'new_driver' ? 'person_add' : 'sync_alt'"
                  size="small"
                  :color="item.type === 'new_driver' ? 'success' : 'primary'"
                />
              </VaListItemSection>
              <VaListItemSection class="min-w-0">
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {{ item.message }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {{ item.relativeTime }}
                </p>
              </VaListItemSection>
            </VaListItem>
            <VaListSeparator class="mx-4" />
          </template>
          <VaListItem v-if="!store.recentNotifications.length" class="py-8 text-center">
            <VaListItemSection>
              <p class="text-sm text-gray-500 dark:text-gray-400">No recruitment notifications yet</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Events will appear here when drivers register or change state
              </p>
            </VaListItemSection>
          </VaListItem>
        </VaList>
      </section>
      <div
        v-if="store.recentNotifications.length"
        class="p-2 border-t border-gray-200 dark:border-gray-700 flex justify-end"
      >
        <VaButton size="small" preset="plain" @click="markAllAsSeen">Mark all as seen</VaButton>
        <VaButton size="small" preset="primary" :to="{ name: 'recruiter-dashboard' }" class="ml-2">
          View Dashboard
        </VaButton>
      </div>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useRecruitmentNotificationsStore } from '@/stores/recruitmentNotifications'
import type { RecruitmentNotification } from '@/stores/recruitmentNotifications'

const store = useRecruitmentNotificationsStore()
const router = useRouter()

const unreadCount = computed(() => store.unreadCount)

const TIME_NAMES: Record<string, number> = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
}

function getRelativeTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime()
  if (diff < TIME_NAMES.minute) return 'Just now'
  if (diff < TIME_NAMES.hour) return `${Math.floor(diff / TIME_NAMES.minute)}m ago`
  if (diff < TIME_NAMES.day) return `${Math.floor(diff / TIME_NAMES.hour)}h ago`
  if (diff < TIME_NAMES.week) return `${Math.floor(diff / TIME_NAMES.day)}d ago`
  return new Date(ts).toLocaleDateString()
}

const notificationsWithRelativeTime = computed(() =>
  store.recentNotifications.map((n) => ({
    ...n,
    relativeTime: getRelativeTime(n.timestamp),
  })),
)

function handleNotificationClick(item: RecruitmentNotification) {
  store.markAsSeen(item.id)
  const driverId = item.data.driver_id
  if (driverId) {
    router.push({
      name: 'applicant-detail-by-driver-id',
      params: { driverId },
      query: { returnTo: 'recruiter-dashboard' },
    })
  } else {
    router.push({ name: 'recruiter-dashboard' })
  }
}

function markAllAsSeen() {
  store.markAllAsSeen()
}
</script>

<style lang="scss" scoped>
.recruitment-notification-dropdown {
  cursor: pointer;

  .va-dropdown__anchor {
    display: inline-block;
  }
}
</style>
